import { useEffect, useState } from 'preact/hooks';
import { useAuthStore } from '@xantory/store';
import { refreshAccount } from './client';

/**
 * Proactive session keepalive for the ACTIVE account (R-303).
 *
 * **Why this is keyed to the ACCESS token, not the refresh token.** The obvious
 * reading — "refresh before the refresh token expires" — points at 7 days and buys
 * nothing: the backend's refresh window slides forward on every rotation, so an
 * account that is being used never reaches it. What actually lapses on a console
 * left open all day is the **15-minute access token**, and the cost of letting it
 * lapse is that the next request 401s, refreshes, and retries — a stumble on the
 * first action after every idle stretch. Rotating just before expiry removes it.
 *
 * **Why this is not a bare `setInterval`.** Background tabs get their timers
 * throttled and a suspended laptop fires none at all, so a wall display that sleeps
 * overnight wakes with a schedule that silently drifted. The timer is therefore the
 * fast path only; `visibilitychange` and `online` are what actually cover waking up,
 * and the 401-refresh-retry inside `request()` remains the correctness backstop
 * underneath both.
 *
 * **Only the active account is polled** (the user's explicit choice): the accounts
 * sitting in the background make no requests, so their windows run down and they
 * lapse. Switching to one validates it first — see `useSwitchAccount`.
 */

/** Rotate this far before the access token actually lapses. */
export const REFRESH_SKEW_MS = 90_000;
/** Used when the stored session predates recorded expiries (a v1-store session):
 *  just under the 15-minute access token, so it still never lapses in use. */
export const FALLBACK_REFRESH_MS = 13 * 60_000;
/** Never schedule tighter than this — a lapsed or unparsable expiry must not
 *  become a hot loop against the gateway. */
export const MIN_DELAY_MS = 5_000;
/** Ceiling on the offline/failure backoff. */
const MAX_BACKOFF_MS = 60_000;
/** R-366: tabs of one browser hydrate the same `accessExp`, so without this every
 *  tab would fire its rotation at the same millisecond and race the backend's
 *  single-use token. Spread the schedule; the backend's replay grace covers the rest. */
export const JITTER_MAX_MS = 30_000;

/** A per-mount offset in `[0, JITTER_MAX_MS)`. Exported so a test can pin it. */
export function jitterMs(random: () => number = Math.random): number {
  return Math.floor(Math.min(Math.max(random(), 0), 0.999_999) * JITTER_MAX_MS);
}

/** Milliseconds until the active token should be rotated. Pure — unit-tested. */
export function msUntilRefresh(accessExp: string | undefined, now: number): number {
  if (!accessExp) return FALLBACK_REFRESH_MS;
  const exp = Date.parse(accessExp);
  if (Number.isNaN(exp)) return FALLBACK_REFRESH_MS;
  return Math.max(MIN_DELAY_MS, exp - REFRESH_SKEW_MS - now);
}

/** True when the token is inside its skew window (or already past it) — the test a
 *  wake-up event applies before spending a request. Pure — unit-tested. */
export function isRefreshDue(accessExp: string | undefined, now: number): boolean {
  return msUntilRefresh(accessExp, now) <= MIN_DELAY_MS;
}

/** Backoff after a failed rotation. Pure — unit-tested. */
export function retryDelayMs(attempt: number): number {
  return Math.min(MAX_BACKOFF_MS, MIN_DELAY_MS * 2 ** Math.max(0, attempt - 1));
}

export function useSessionKeepalive(): void {
  const activeId = useAuthStore((s) => s.activeId);
  const accessExp = useAuthStore((s) => (s.activeId ? s.accounts[s.activeId]?.accessExp : undefined));
  // A failed rotation leaves `accessExp` untouched, so nothing would re-trigger the
  // effect and the keepalive would quietly stop after one offline blip. Bumping this
  // is what schedules the retry.
  const [attempt, setAttempt] = useState(0);
  // Fixed for this mount, so the schedule is jittered once rather than re-rolled per tick.
  const [jitter] = useState(() => jitterMs());

  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;

    const delay =
      attempt === 0
        ? Math.max(MIN_DELAY_MS, msUntilRefresh(accessExp, Date.now()) - jitter)
        : retryDelayMs(attempt);
    const timer = setTimeout(() => {
      void refreshAccount(activeId).then((token) => {
        if (cancelled) return;
        // Success resets the backoff; the new `accessExp` re-runs this effect and
        // schedules the next rotation on its own.
        setAttempt((n) => (token ? 0 : n + 1));
      });
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeId, accessExp, attempt]);

  // Waking up is the case a timer cannot cover.
  useEffect(() => {
    if (!activeId) return;
    const wake = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      const exp = useAuthStore.getState().accounts[activeId]?.accessExp;
      if (isRefreshDue(exp, Date.now())) void refreshAccount(activeId);
    };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);
    return () => {
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', wake);
    };
  }, [activeId]);
}
